import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) return setError("Password must be at least 6 characters.");
        if (password !== confirm) return setError("Passwords do not match.");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated. Please sign in.");
            navigate("/login");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#990000] mb-6">
                    <ArrowLeft size={16} /> Back to sign in
                </Link>
                <div className="bg-white border border-stone-200 rounded-xl p-8">
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">Set new password</div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-2">
                        Choose a new password
                    </h1>
                    <p className="text-stone-500 mb-6 text-sm">
                        Make it at least 6 characters. Use something you don't use elsewhere.
                    </p>

                    {!token ? (
                        <div data-testid="reset-no-token" className="text-sm text-[#990000] bg-[#990000]/5 border border-[#990000]/20 rounded-lg px-3 py-2">
                            Missing reset token. Please request a new link.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">New password</Label>
                                <Input type="password" data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                                       placeholder="At least 6 characters" minLength={6}
                                       className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Confirm password</Label>
                                <Input type="password" data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                       placeholder="Re-enter password" minLength={6}
                                       className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                            </div>
                            {error && <div data-testid="reset-error" className="text-sm text-[#990000] bg-[#990000]/5 border border-[#990000]/20 rounded-lg px-3 py-2">{error}</div>}
                            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                                    className="w-full h-11 bg-[#990000] hover:bg-[#7A0000] text-white font-medium">
                                {loading ? "Updating…" : "Update password"}
                            </Button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
